import { useState, useEffect, useRef, useCallback, lazy, Suspense, memo, useMemo } from "react";
import { logAudit } from "../utils/auditLog";
import epiphanyLogo from "../assets/epiphanymd-logo.svg";
import ErrorBoundary from "./ErrorBoundary";
import { SCHEMES } from "../data/colorSchemes";
import ImagingPage from "./ImagingPage";
import MedicationsPage from "./MedicationsPage";
import LabsPage, { LabCriticalBanner } from "./LabsPage";
import FaxPage, { MOCK_FAX_INBOX } from "./FaxPage";
import MessagesPage, { MOCK_MESSAGES, CURRENT_USER as MSG_CURRENT_USER } from "./MessagesPage";
import PatientInboxPage, { MOCK_PORTAL_INBOX, getPortalUnreadCount } from "./PatientInboxPage";
import CostEstimatePage from "./CostEstimatePage";
import PortalManagement from "./PortalManagement";
import { supabase } from "../lib/supabase";
import { getCurrentPracticeId, getCurrentUserInfo } from "../lib/api";
import { auth } from "../lib/firebase";
import { signOut as fbSignOut } from "firebase/auth";
import { db } from "../lib/db";
import { storage } from "../lib/storage";
// localStorage demo flag is ignored on the production domain so a stale flag
// from a prior demo session never bypasses the login screen.
const localDemoEnabled = () =>
  localStorage.getItem('epiphany_demo_mode') === 'true' &&
  window.location.hostname !== 'epiphanymd.com';
import { MOCK_SCHEDULE } from "../data/mockData";
import { useSessionTimeout, SessionTimeoutModal, FeedbackButton } from "../pages/SessionWidgets";
import { AppProvider, useApp } from "../context/AppContext";
import { hasPermission } from "../utils/roleGates";
import { NotificationBell } from "../pages/SchedulePage";
import Dashboard from "../pages/DashboardPage";
import SchedulePageComponent from "../pages/SchedulePage";
import RoomingBoard from "./RoomingBoard";
import PatientsPage, { NewPatientPage } from "../pages/PatientsPage";
import NotesPage from "../pages/NotesPage";
import "../styles/emrShell.css";

// ─── Lazy-loaded page chunks ──────────────────────────────────────────────────
// PatientChartPage (~450KB source, the largest file in the app) + DocumentsPage
// (~360KB) were code-split 2026-07-08 — the "40s first load" fix. Both
// PatientChart lazies resolve from the SAME chunk (one network fetch).
const PatientChart  = lazy(() => import("../pages/PatientChartPage"));
const CheckInModal  = lazy(() => import("../pages/PatientChartPage").then((m) => ({ default: m.CheckInModal })));
const DocumentsPage = lazy(() => import("./DocumentsPage"));
const ARDashboard        = lazy(() => import("./ARDashboard"));
const AnalyticsPage      = lazy(() => import("./AnalyticsPage"));
const DenialWorklist     = lazy(() => import("./DenialWorklist"));
const CodingPage         = lazy(() => import("./CodingPage"));
const EligibilityPage    = lazy(() => import("./EligibilityPage"));
const PaymentPostingPage = lazy(() => import("./PaymentPostingPage"));
const ImportPage         = lazy(() => import("./ImportPage"));
const SettingsPage       = lazy(() => import("./SettingsPage"));
const ASCModule          = lazy(() => import("./ASCModule"));
const ASCAnalyticsPage   = lazy(() => import("./ASCAnalyticsPage"));
const ConsultsPage       = lazy(() => import("./ConsultsPage"));
const OrderSetsPage      = lazy(() => import("./OrderSetsPage"));
const PreferenceCardsPage = lazy(() => import("./PreferenceCardsPage"));
const VirtualPhoneModule  = lazy(() => import("./VirtualPhoneModule"));
const OutcomesPage        = lazy(() => import("./OutcomesPage"));
const TrialDashboard      = lazy(() => import("../pages/TrialDashboard"));
const ChargeTicketPage    = lazy(() => import("../pages/ChargeTicketPage"));
const StatementsPage      = lazy(() => import("../pages/StatementsPage"));
const MIPSPage            = lazy(() => import("../pages/MIPSPage"));
const BillingQueue        = lazy(() => import("../pages/BillingQueue"));
const ClaimsStatusPage    = lazy(() => import("../pages/ClaimsStatusPage"));
const PendingOrdersPage   = lazy(() => import("../pages/PendingOrdersPage"));

const PageLoader = <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', color: 'var(--text-3)', fontSize: 13 }}>Loading…</div>;

// ─── THEME ────────────────────────────────────────────────────────────────────
function applyTheme({ mode = 'dark', scheme = 'teal' } = {}) {
  const sc = SCHEMES[scheme] || SCHEMES.teal;
  const r = document.documentElement;
  r.style.setProperty('--teal',    sc.primary);
  r.style.setProperty('--teal-lt', sc.lt);
  r.style.setProperty('--teal-md', sc.md);
  if (mode === 'light') {
    r.style.setProperty('--navy',    '#f8fafc');
    r.style.setProperty('--navy-2',  '#ffffff');
    r.style.setProperty('--navy-3',  '#f1f5f9');
    r.style.setProperty('--navy-4',  '#e2e8f0');
    r.style.setProperty('--text-1',  '#0f172a');
    r.style.setProperty('--text-2',  '#475569');
    r.style.setProperty('--text-3',  '#94a3b8');
    r.style.setProperty('--border',  'rgba(0,0,0,0.08)');
    r.style.setProperty('--border-2','rgba(0,0,0,0.14)');
    r.style.setProperty('--body-bg', '#f8fafc');
    r.style.setProperty('--logo-filter', 'brightness(0.15)');
    r.style.setProperty('--pt-name', '#0f172a'); // patient name: dark ink on light theme
  } else {
    ['--navy','--navy-2','--navy-3','--navy-4','--text-1','--text-2','--text-3','--border','--border-2','--body-bg','--logo-filter','--pt-name']
      .forEach(v => r.style.removeProperty(v));
  }
}

function ThemePopover({ theme, onChange, onClose }) {
  const ref = useRef();
  const mode = theme?.mode || 'dark';
  const scheme = theme?.scheme || 'teal';

  useEffect(() => {
    const handler = e => { if (!ref.current?.contains(e.target)) onClose(); };
    const t = setTimeout(() => document.addEventListener('mousedown', handler), 60);
    return () => { clearTimeout(t); document.removeEventListener('mousedown', handler); };
  }, [onClose]);

  return (
    <div ref={ref} style={{
      position: 'absolute', bottom: 'calc(100% + 6px)', left: 0, right: 0,
      background: 'var(--navy-3)', border: '1px solid var(--border-2)',
      borderRadius: 10, padding: 14, zIndex: 300,
      boxShadow: '0 -4px 24px rgba(0,0,0,0.35)',
    }}>
      <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-1)', marginBottom: 10 }}>Appearance</div>
      <div style={{ display: 'flex', background: 'var(--navy-4)', borderRadius: 8, padding: 3, marginBottom: 12 }}>
        {[{ id: 'dark', icon: '🌙', label: 'Dark' }, { id: 'light', icon: '☀️', label: 'Light' }].map(m => (
          <button key={m.id} onClick={() => onChange({ mode: m.id, scheme })}
            style={{
              flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 5,
              padding: '5px 0', borderRadius: 6, border: 'none', cursor: 'pointer', fontSize: 12,
              background: mode === m.id ? 'var(--navy-2)' : 'transparent',
              color: mode === m.id ? 'var(--text-1)' : 'var(--text-3)',
              fontWeight: mode === m.id ? 500 : 400,
            }}>
            {m.icon} {m.label}
          </button>
        ))}
      </div>
      <div style={{ fontSize: 10, color: 'var(--text-3)', textTransform: 'uppercase', letterSpacing: '0.06em', fontWeight: 500, marginBottom: 8 }}>Color Scheme</div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 6 }}>
        {Object.entries(SCHEMES).map(([key, sc]) => (
          <div key={key} onClick={() => onChange({ mode, scheme: key })}
            style={{ textAlign: 'center', cursor: 'pointer' }}>
            <div style={{
              width: 28, height: 28, borderRadius: '50%', background: sc.primary,
              margin: '0 auto 4px', display: 'flex', alignItems: 'center', justifyContent: 'center',
              color: '#fff', fontSize: 11, fontWeight: 700,
              boxShadow: scheme === key ? `0 0 0 2px var(--navy-3), 0 0 0 4px ${sc.primary}` : 'none',
              transition: 'box-shadow 150ms',
            }}>
              {scheme === key ? '✓' : ''}
            </div>
            <div style={{ fontSize: 9, color: 'var(--text-3)', lineHeight: 1.2 }}>{sc.label.split(' ')[0]}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── COMMAND BAR ──────────────────────────────────────────────────────────────
const RECENT_PATIENTS_KEY = 'epiphany_recent_patients';

function CommandBar({ onPatientSelect, onNavigate, practiceId }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [sel, setSel] = useState(0);
  const [recentPatients, setRecentPatients] = useState([]);
  const [patientResults, setPatientResults] = useState([]);
  const inputRef = useRef(null);

  // Load recent patients from localStorage on mount (instant display)
  useEffect(() => {
    try {
      const cached = JSON.parse(localStorage.getItem(RECENT_PATIENTS_KEY) || '[]');
      if (Array.isArray(cached)) setRecentPatients(cached.slice(0, 10));
    } catch {}
  }, []);

  // Refresh recent patients from Supabase when command bar opens
  useEffect(() => {
    if (!open) return;
    (async () => {
      try {
        const fallbackPid = await getCurrentPracticeId();
        const pid = practiceId || fallbackPid;
        if (!pid) return;
        const { uid } = await getCurrentUserInfo();
        if (!uid) return;
        const { data: rows } = await db
          .from('recent_patients_viewed')
          .select('patient_id, viewed_at')
          .eq('practice_id', pid)
          .eq('provider_id', uid)
          .order('viewed_at', { ascending: false })
          .limit(10);
        if (!rows?.length) return;
        // Fetch real patient rows from Supabase by UUID — filter out any stale mock IDs (P001, etc.)
        const ids = [...new Set(rows.map(r => r.patient_id).filter(id => id && /^[0-9a-f-]{36}$/i.test(id)))];
        if (!ids.length) return;
        const { data: pts } = await db
          .from('patients')
          .select('id, first_name, last_name, mrn, date_of_birth')
          .in('id', ids);
        if (!pts?.length) return;
        const ptMap = {};
        pts.forEach(p => { ptMap[p.id] = p; });
        const ordered = rows
          .map(r => ptMap[r.patient_id])
          .filter(Boolean);
        const unique = [...new Map(ordered.map(p => [p.id, p])).values()].slice(0, 10);
        setRecentPatients(unique);
        localStorage.setItem(RECENT_PATIENTS_KEY, JSON.stringify(unique));
      } catch {}
    })();
  }, [open, practiceId]);

  // Live patient search against Supabase (debounced 200ms)
  useEffect(() => {
    if (query.length < 2) { setPatientResults([]); return; }
    let cancelled = false;
    const t = setTimeout(async () => {
      try {
        const fallbackPid = await getCurrentPracticeId();
        const pid = practiceId || fallbackPid;
        if (!pid) return;
        const { data } = await db
          .from('patients')
          .select('id, first_name, last_name, mrn, date_of_birth')
          .eq('practice_id', pid)
          .or(`first_name.ilike.%${query}%,last_name.ilike.%${query}%,mrn.ilike.%${query}%`)
          .limit(6);
        if (!cancelled) setPatientResults(data || []);
      } catch { if (!cancelled) setPatientResults([]); }
    }, 200);
    return () => { cancelled = true; clearTimeout(t); };
  }, [query, practiceId]); // eslint-disable-line

  useEffect(() => {
    const handler = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === "k") { e.preventDefault(); setOpen(true); }
      if (e.key === "Escape") setOpen(false);
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, []);

  useEffect(() => { if (open) { setQuery(""); setSel(0); setPatientResults([]); setTimeout(() => inputRef.current?.focus(), 50); } }, [open]);

  const navResults = [
    { label: "Dashboard", icon: "⬡", action: () => onNavigate("dashboard") },
    { label: "New Patient", icon: "＋", action: () => onNavigate("new-patient") },
    { label: "Schedule", icon: "◫", action: () => onNavigate("schedule") },
    { label: "Patients", icon: "◎", action: () => onNavigate("patients") },
    { label: "Calendar & Billing", icon: "📅", action: () => onNavigate("calendar") },
    { label: "Transcribe Visit", icon: "🎙", action: () => onNavigate("transcribe") },
    { label: "AR Analytics", icon: "◈", action: () => onNavigate("analytics") },
    { label: "Denial Worklist", icon: "⚑", action: () => onNavigate("denials") },
    { label: "Charge Tickets", icon: "🧾", action: () => onNavigate("charge-tickets") },
  ].filter(r => !query || r.label.toLowerCase().includes(query.toLowerCase()));

  const allResults = [
    ...patientResults.map(p => ({ type: "patient", label: `${p.first_name} ${p.last_name}`, sub: p.mrn, icon: "◎", action: () => { onPatientSelect(p); setOpen(false); } })),
    ...navResults.map(r => ({ type: "nav", label: r.label, icon: r.icon, action: () => { r.action(); setOpen(false); } })),
  ];

  const handleKey = (e) => {
    if (e.key === "ArrowDown") { e.preventDefault(); setSel(s => Math.min(s + 1, allResults.length - 1)); }
    if (e.key === "ArrowUp")   { e.preventDefault(); setSel(s => Math.max(s - 1, 0)); }
    if (e.key === "Enter" && allResults[sel]) { allResults[sel].action(); }
  };

  if (!open) return (
    <div className="cmd-trigger" onClick={() => setOpen(true)}>
      <span className="cmd-search-icon">⌕</span>
      <span>Search patients, navigate…</span>
      <span className="cmd-kbd">⌘K</span>
    </div>
  );

  return (
    <div className="cmd-overlay" onClick={() => setOpen(false)}>
      <div className="cmd-modal" onClick={e => e.stopPropagation()}>
        <div className="cmd-input-wrap">
          <span className="cmd-search-icon">⌕</span>
          <input ref={inputRef} className="cmd-input" placeholder="Search patients or navigate…" value={query} onChange={e => { setQuery(e.target.value); setSel(0); }} onKeyDown={handleKey} />
        </div>
        <div className="cmd-results">
          {/* Empty query: show recent patients */}
          {!query && recentPatients.length > 0 && (
            <>
              <div className="cmd-section-label">Recent Patients</div>
              {recentPatients.map((p, i) => (
                <div key={p.id} className={`cmd-result${i === sel ? " selected" : ""}`}
                  onClick={() => { onPatientSelect(p); setOpen(false); }}>
                  <span className="cmd-result-icon">◎</span>
                  <span>{p.first_name} {p.last_name}</span>
                  <span className="cmd-result-sub">{p.mrn}{p.dob ? ` · DOB ${p.dob}` : ""}</span>
                </div>
              ))}
              <div className="cmd-section-label">Navigate</div>
              {navResults.map((r, i) => {
                const idx = recentPatients.length + i;
                return (
                  <div key={r.label} className={`cmd-result${idx === sel ? " selected" : ""}`}
                    onClick={() => { r.action(); setOpen(false); }}>
                    <span className="cmd-result-icon">{r.icon}</span>
                    <span>{r.label}</span>
                  </div>
                );
              })}
            </>
          )}
          {/* Has query: show search results */}
          {query && (
            <>
              {allResults.length === 0 && <div className="cmd-empty">No results</div>}
              {patientResults.length > 0 && <div className="cmd-section-label">Patients</div>}
              {allResults.map((r, i) => (
                <div key={i} className={`cmd-result${i === sel ? " selected" : ""}`} onClick={r.action}>
                  <span className="cmd-result-icon">{r.icon}</span>
                  <span>{r.label}</span>
                  {r.sub && <span className="cmd-result-sub">{r.sub}</span>}
                </div>
              ))}
            </>
          )}
          {/* No query, no recent patients: show navigation only */}
          {!query && recentPatients.length === 0 && (
            <>
              <div className="cmd-section-label">Navigate</div>
              {navResults.map((r, i) => (
                <div key={r.label} className={`cmd-result${i === sel ? " selected" : ""}`}
                  onClick={() => { r.action(); setOpen(false); }}>
                  <span className="cmd-result-icon">{r.icon}</span>
                  <span>{r.label}</span>
                </div>
              ))}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── SIDEBAR ──────────────────────────────────────────────────────────────────
const Sidebar = memo(function Sidebar({ active, onNav, theme, onThemeChange, onSignOut, className, practiceId, subscriptionStatus, voicemailEnabled, provider, onProviderPhotoUpdate }) {
  const isDemoMode = process.env.REACT_APP_DEMO_MODE === 'true';
  const appCtx = useApp();
  const userRole = appCtx?.userRole;
  const [showTheme, setShowTheme] = useState(false);
  const [permDeniedMsg, setPermDeniedMsg] = useState(false);
  const [avatarHover, setAvatarHover] = useState(false);
  const [photoUploading, setPhotoUploading] = useState(false);
  const photoInputRef = useRef(null);

  const handlePhotoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !provider?.id || !practiceId) {
      console.log('[PhotoUpload] Early exit — file:', !!file, '| provider.id:', provider?.id, '| practiceId:', practiceId);
      return;
    }
    setPhotoUploading(true);
    try {
      const { compressImage } = await import('../utils/imageCompressor');
      const compressed = await compressImage(file, 400, 0.85);
      const path = `${practiceId}/${provider.id}.jpg`;
      console.log('[PhotoUpload] Uploading to path:', path);
      await storage.uploadFile('provider-photos', path, compressed, 'image/jpeg');
      const { data: updateData, error: updateErr } = await db.from('providers').update({ photo_url: path }).eq('id', provider.id);
      console.log('[PhotoUpload] providers update result — data:', updateData, '| error:', updateErr);
      const signedUrl = await storage.getSignedUrl('provider-photos', path, 3600);
      console.log('[PhotoUpload] Signed URL result — url:', signedUrl);
      if (onProviderPhotoUpdate) onProviderPhotoUpdate({ photo_url: path, signedPhotoUrl: signedUrl });
    } catch (err) {
      console.error('[PhotoUpload] Failed:', err?.message, err);
    }
    setPhotoUploading(false);
    e.target.value = '';
  };

  // Derived provider display values
  const providerInitials = provider
    ? ((provider.first_name?.[0] ?? '') + (provider.last_name?.[0] ?? '')).toUpperCase() || '?'
    : '?';
  const providerDisplayName = provider?.first_name && provider?.last_name
    ? `Dr. ${provider.first_name[0]}. ${provider.last_name}`
    : provider?.email ?? 'Provider';
  const providerRole = provider?.specialty ?? 'Orthopedic Surgery';

  const navItems = [
    { id: "dashboard", icon: "⬡", label: "Dashboard" },
    { id: "schedule", icon: "◫", label: "Schedule", badge: isDemoMode ? (MOCK_SCHEDULE.filter(a => a.status === "checked-in").length || null) : null },
    { id: "rooming", icon: "🚪", label: "Rooming" },
    { id: "patients", icon: "◎", label: "Patients" },
    { id: "notes", icon: "◻", label: "Notes" },
    { id: "coding",     icon: "⋮", label: "Coding",          permission: "viewCoding" },
    { id: "calendar",   icon: "📅", label: "Calendar" },
    { id: "transcribe", icon: "🎙", label: "Transcribe",      permission: "createNotes" },
    { id: "analytics",       icon: "◈", label: "AR Analytics",    permission: "viewARAnalytics" },
    { id: "mips",            icon: "✓", label: "MIPS Quality",     permission: "viewARAnalytics" },
    { id: "denials",         icon: "⚑", label: "Denial Worklist", permission: "viewBilling" },
    { id: "charge-tickets",  icon: "🧾", label: "Charge Tickets",  permission: "viewBilling" },
    { id: "statements",      icon: "📑", label: "Statements",      permission: "viewBilling" },
    { id: "billing-queue",   icon: "📤", label: "Billing Queue",    permission: "editBilling" },
    { id: "claims-status",   icon: "📋", label: "Claims Status",    permission: "viewBilling" },
    { id: "eligibility",     icon: "🛡", label: "Eligibility" },
    { id: "payments",        icon: "◎", label: "Payment Posting",  permission: "editBilling" },
    { id: "imaging",      icon: "🩻", label: "Imaging" },
    { id: "medications",  icon: "💊", label: "Medications" },
    { id: "labs",         icon: "🧪", label: "Labs" },
    { id: "documents",    icon: "📂", label: "Documents" },
    { id: "fax",          icon: "📠", label: "Fax",      badge: isDemoMode ? (MOCK_FAX_INBOX.filter(f => f.unread).length || null) : null },
    { id: "messages",     icon: "💬", label: "Messages", badge: isDemoMode ? (MOCK_MESSAGES.filter(m => m.sender !== MSG_CURRENT_USER && !m.is_read).length || null) : null },
    { id: "patient-inbox", icon: "📨", label: "Patient Messages", badge: isDemoMode ? (getPortalUnreadCount(MOCK_PORTAL_INBOX) || null) : null, badgeColor: "badge-amber" },
    { id: "cost",         icon: "🧮", label: "Surgery Cost Calculator" },
  ];
  const insightsItems = [
    { id: "productivity", icon: "📊", label: "Provider Analytics" },
    { id: "outcomes",     icon: "📈", label: "Outcomes" },
  ];
  const ordersItems = [
    { id: "consults",          icon: "📋", label: "Consult Request" },
    { id: "pending-orders",    icon: "📋", label: "Pending Orders" },
    { id: "order-sets",        icon: "📦", label: "Order Sets" },
    { id: "preference-cards",  icon: "🃏", label: "Preference Cards" },
  ];
  const ascItems = [
    { id: "asc-schedule",      icon: "🗓", label: "OR Schedule" },
    { id: "asc-board",         icon: "⬡", label: "Case Board" },
    { id: "asc-documentation", icon: "📝", label: "Documentation" },
    { id: "asc-discharge",     icon: "📄", label: "Discharge" },
    { id: "asc-implants",      icon: "🔩", label: "Implant Log" },
    { id: "asc-analytics",     icon: "📊", label: "ASC Analytics" },
  ];
  const phoneItems = [
    { id: "phone-calls",     icon: "📞", label: "Call Log" },
    { id: "phone-sms",       icon: "💬", label: "SMS / Text" },
    { id: "phone-voicemail", icon: "📱", label: "Voicemails" },
  ];
  const adminItems = [
    { id: "import",   icon: "📥", label: "Data Import" },
    { id: "portal",   icon: "🌐", label: "Patient Portal" },
    { id: "settings", icon: "⚙️", label: "Settings" },
  ];

  const renderItems = (items) => items.map(item => {
    const locked = item.permission && !hasPermission(userRole, item.permission);
    return (
      <div
        key={item.id}
        className={`nav-item ${!locked && active === item.id ? "active" : ""}`}
        style={locked ? { opacity: 0.45, cursor: 'not-allowed' } : undefined}
        title={locked ? "You don't have permission for this section" : undefined}
        onClick={() => {
          if (locked) {
            setPermDeniedMsg(true);
            setTimeout(() => setPermDeniedMsg(false), 2500);
          } else {
            onNav(item.id);
          }
        }}
      >
        <span className="nav-icon">{locked ? "🔒" : item.icon}</span>
        <span>{item.label}</span>
        {!locked && item.badge ? <span className={`nav-badge ${item.badgeColor || ""}`}>{item.badge}</span> : null}
      </div>
    );
  });

  return (
    <nav className={`sidebar${className ? " " + className : ""}`}>
      {permDeniedMsg && (
        <div style={{ position: 'absolute', bottom: 80, left: 8, right: 8, zIndex: 999, background: 'var(--navy-4)', border: '1px solid var(--border-2)', borderRadius: 8, padding: '8px 12px', fontSize: 12, color: 'var(--text-2)', textAlign: 'center', boxShadow: '0 4px 16px rgba(0,0,0,0.4)' }}>
          🔒 You don't have permission for this section
        </div>
      )}
      <div className="sidebar-logo">
        <img src={epiphanyLogo} alt="Epiphany MD" style={{ height: 36, maxWidth: 180, objectFit: "contain", filter: "var(--logo-filter, none)" }} />
      </div>
      <div className="sidebar-section">
        <div className="sidebar-label">Workspace</div>
        {renderItems(navItems)}
        <div className="sidebar-label" style={{ marginTop: 12 }}>Insights</div>
        {renderItems(insightsItems)}
        <div className="sidebar-label" style={{ marginTop: 12 }}>Orders</div>
        {renderItems(ordersItems)}
        {/* Virtual Phone: full shell in demo (mocks). In production, calling/SMS
            threads stay hidden until a telephony BAA exists; the Voicemails
            inbox lights up when practices.voicemail_enabled is flipped (built
            end-to-end, BAA-gated). Real comms today: Patient Messages (portal)
            + SMS nudges + tel: click-to-call. */}
        {(isDemoMode || voicemailEnabled) && (
          <>
            <div className="sidebar-label" style={{ marginTop: 12 }}>Phone</div>
            {renderItems(isDemoMode ? phoneItems : phoneItems.filter(i => i.id === "phone-voicemail"))}
          </>
        )}
        <div className="sidebar-label" style={{ marginTop: 12 }}>Surgery</div>
        {renderItems(ascItems)}
        <div className="sidebar-label" style={{ marginTop: 12 }}>Admin</div>
        {renderItems(adminItems)}
        <a
          href="/faq"
          className="nav-item"
          style={{ textDecoration: 'none' }}
        >
          <span className="nav-icon">❓</span>
          <span>FAQ</span>
        </a>
        {subscriptionStatus === "trial" && (
          <>
            <div className="sidebar-label" style={{ marginTop: 12 }}>Trial</div>
            {renderItems([{ id: "trial-dashboard", icon: "📊", label: "Trial Dashboard" }])}
          </>
        )}
      </div>
      <div className="sidebar-footer" style={{ position: "relative" }}>
        {showTheme && (
          <ThemePopover
            theme={theme}
            onChange={t => { applyTheme(t); onThemeChange(t); }}
            onClose={() => setShowTheme(false)}
          />
        )}
        <div style={{ display: "flex", alignItems: "center", gap: 6, minWidth: 0 }}>
          <div className="user-pill" style={{ flex: 1, minWidth: 0, marginBottom: 0 }}>
            <div
              className="avatar"
              style={{ position: 'relative', cursor: 'pointer', overflow: 'hidden', flexShrink: 0 }}
              onMouseEnter={() => setAvatarHover(true)}
              onMouseLeave={() => setAvatarHover(false)}
              onClick={() => !photoUploading && photoInputRef.current?.click()}
              title="Upload profile photo"
            >
              {provider?.signedPhotoUrl
                ? <img src={provider.signedPhotoUrl} alt={providerDisplayName} style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '50%' }} />
                : providerInitials
              }
              {(avatarHover || photoUploading) && (
                <div style={{
                  position: 'absolute', inset: 0, borderRadius: '50%',
                  background: 'rgba(0,0,0,0.5)', display: 'flex',
                  alignItems: 'center', justifyContent: 'center',
                  fontSize: 14, color: '#fff',
                }}>
                  {photoUploading ? '⏳' : '📷'}
                </div>
              )}
            </div>
            <input
              ref={photoInputRef}
              type="file"
              accept="image/*"
              style={{ display: 'none' }}
              onChange={handlePhotoUpload}
            />
            <div className="user-info" style={{ minWidth: 0, overflow: 'hidden' }}>
              <div className="user-name" style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{providerDisplayName}</div>
              <div className="user-role" style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{providerRole}</div>
            </div>
          </div>
          <button
            onClick={() => setShowTheme(s => !s)}
            title="Appearance"
            style={{
              width: 32, height: 32, flexShrink: 0,
              display: "flex", alignItems: "center", justifyContent: "center",
              background: showTheme ? "var(--teal-lt)" : "none",
              border: `1px solid ${showTheme ? "var(--teal)" : "var(--border)"}`,
              borderRadius: 6, color: showTheme ? "var(--teal)" : "var(--text-3)",
              padding: 0, cursor: "pointer", fontSize: 14,
              transition: "all var(--transition)",
            }}>
            🎨
          </button>
          {onSignOut && (
            <button
              onClick={onSignOut}
              title="Sign Out"
              style={{
                width: 32, height: 32, flexShrink: 0,
                display: "flex", alignItems: "center", justifyContent: "center",
                background: "none", border: "1px solid var(--border)",
                borderRadius: 6, color: "var(--text-3)",
                padding: 0, cursor: "pointer", fontSize: 14,
                transition: "all var(--transition)",
              }}>
              ⏻
            </button>
          )}
        </div>
        {practiceId && (
          <div style={{ marginTop: 8, padding: "6px 10px", background: "var(--navy-3)", borderRadius: 6, border: "1px solid var(--border)" }}>
            <div style={{ fontSize: 10, color: "var(--text-3)", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 1 }}>Practice</div>
            <div style={{ fontSize: 11, color: "var(--text-2)", fontFamily: "var(--mono)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{practiceId}</div>
          </div>
        )}
      </div>
    </nav>
  );
});

// ─── MOCK_PATIENTS re-export for backward compatibility ───────────────────────
export { MOCK_PATIENTS } from "../data/mockData";

// ─── MAIN SHELL ───────────────────────────────────────────────────────────────
export default function EMRShell({ calendarPage, transcribePage, session, practiceId }) {
  const [page, setPage] = useState("dashboard");
  const [activePatient, setActivePatient] = useState(null);
  const [openCharts, setOpenCharts] = useState([]); // multi-chart tabs (max 3, kept mounted)
  const [checkingIn, setCheckingIn] = useState(false);
  const [ackedLabs, setAckedLabs]   = useState(new Set());
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);
  const [voicemailEnabled, setVoicemailEnabled] = useState(false); // practices.voicemail_enabled (BAA gate)
  const [provider, setProvider] = useState(null); // { id, first_name, last_name, specialty, photo_url }
  const [isDemoMode, setIsDemoMode] = useState(
    () => localDemoEnabled() || process.env.REACT_APP_DEMO_MODE === 'true'
  );
  const [patients, setPatients] = useState([]);
  const [notification, setNotification] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [navParams, setNavParams] = useState({});

  const handleSignOut = useCallback(async () => {
    try {
      const uid = auth.currentUser?.uid;
      if (uid) await logAudit('logout', 'session', uid, null);
    } catch {}
    localStorage.removeItem('epiphany_demo_mode');
    localStorage.removeItem('epiphany_demo_session');
    try { await fbSignOut(auth); } catch {}        // Identity Platform (primary)
    try { await supabase.auth.signOut(); } catch {} // clear any lingering Supabase session
    window.location.href = '/';
  }, []);

  const exitDemo = useCallback(async () => {
    localStorage.removeItem('epiphany_demo_mode');
    setIsDemoMode(false);
    try { await fbSignOut(auth); } catch {}
    try { await supabase.auth.signOut(); } catch {}
    window.location.href = '/';
  }, []);

  // Listen for demo mode toggle from SettingsPage
  useEffect(() => {
    const handler = () => setIsDemoMode(localDemoEnabled());
    window.addEventListener('epiphany-demo-mode-change', handler);
    return () => window.removeEventListener('epiphany-demo-mode-change', handler);
  }, []);

  const { showWarning: sessionWarning, secondsLeft, stayLoggedIn } = useSessionTimeout(handleSignOut);

  const [theme, setTheme] = useState(() => {
    try { return JSON.parse(localStorage.getItem('epiphanymd-theme') || '{}'); } catch { return {}; }
  });
  useEffect(() => { applyTheme(theme); }, []);
  // Cornerstone3D (~1MB) is dynamically imported so DICOM init happens right
  // after mount WITHOUT sitting in the main bundle (code-split 2026-07-08).
  useEffect(() => {
    import("../utils/cornerstoneInit").then((m) => m.initCornerstone()).catch(() => {});
  }, []); // eslint-disable-line

  // Fetch real patients for fax linking, patient search, etc.
  useEffect(() => {
    if (!practiceId) return;
    const fetchPatients = async () => {
      const { data, error } = await db
        .from('patients')
        .select('id, first_name, last_name, mrn, date_of_birth')
        .eq('practice_id', practiceId)
        .order('last_name', { ascending: true });
      if (error) {
        console.error('[EMRShell] failed to fetch patients:', error);
        return;
      }
      setPatients(data || []);
    };
    fetchPatients();
  }, [practiceId]);

  // Fetch subscription status (Trial section) + voicemail gate (Phone section)
  useEffect(() => {
    if (!practiceId || !/^[0-9a-f-]{36}$/i.test(practiceId)) return;
    db.from("practices").select("subscription_status, voicemail_enabled").eq("id", practiceId).maybeSingle()
      .then(({ data }) => {
        if (data?.subscription_status) setSubscriptionStatus(data.subscription_status);
        setVoicemailEnabled(!!data?.voicemail_enabled);
      });
  }, [practiceId]);

  // Fetch current provider record for sidebar card
  useEffect(() => {
    if (!practiceId || !/^[0-9a-f-]{36}$/i.test(practiceId)) return;
    getCurrentUserInfo().then(async ({ email }) => {
      if (!email) return;
      const { data } = await db
        .from('providers')
        .select('id, first_name, last_name, specialty, photo_url')
        .eq('practice_id', practiceId)
        .eq('email', email)
        .maybeSingle();
      if (data) {
        // Resolve signed URL for photo if present
        if (data.photo_url) {
          data.signedPhotoUrl = await storage.getSignedUrl('provider-photos', data.photo_url, 3600).catch(() => null);
        }
        setProvider(data);
      } else {
        // Fallback: use email/session data with no provider record
        setProvider({ email });
      }
    });
  }, [practiceId]);
  const handleThemeChange = useCallback((t) => {
    applyTheme(t);
    setTheme(t);
    localStorage.setItem('epiphanymd-theme', JSON.stringify(t));
  }, []);

  const showNotification = useCallback((msg, type = "info") => {
    const demo = localDemoEnabled() || process.env.REACT_APP_DEMO_MODE === 'true';
    const displayMsg = (demo && type === "save") ? "Demo mode — this would be saved in a live account" : msg;
    setNotification({ msg: displayMsg, type: type === "save" ? "info" : type });
    setTimeout(() => setNotification(null), 3000);
  }, []);

  const [chartInitialTab, setChartInitialTab] = useState(null); // { id, tab } — targeted per-chart tab signal
  const [dialerPatient, setDialerPatient] = useState(null);
  const [incomingCall, setIncomingCall] = useState(null);
  const [smsPatient, setSmsPatient] = useState(null);

  // Close one chart tab; closing the ACTIVE tab activates the most recent
  // remaining chart, and closing the last one returns to the patient list.
  const closeChart = useCallback((id) => {
    setOpenCharts(prev => {
      const next = prev.filter(p => p.id !== id);
      setActivePatient(cur => {
        if (cur?.id !== id) return cur;
        if (next.length) return next[next.length - 1];
        setPage("patients");
        return null;
      });
      return next;
    });
  }, []);

  const handlePatientSelect = useCallback((patient, initialTab = "summary") => {
    // Multi-chart tabs (max 3, mounted-but-hidden so in-progress notes survive
    // switching). Re-selecting an open patient just activates their tab;
    // opening a 4th evicts the oldest tab.
    setOpenCharts(prev => {
      if (prev.some(p => p.id === patient.id)) {
        return prev.map(p => (p.id === patient.id ? patient : p));
      }
      const next = [...prev, patient];
      return next.length > 3 ? next.slice(1) : next;
    });
    setActivePatient(patient);
    // Target the tab signal at THIS chart only — other mounted charts get
    // undefined and keep their current tab (see render: per-chart initialTab).
    setChartInitialTab({ id: patient.id, tab: initialTab });
    setPage("chart");
    logAudit('view', 'patient', patient.id, patient.id, { name: `${patient.first_name || patient.first || ''} ${patient.last_name || patient.last || ''}`.trim() });
    // Record recent patient view: update localStorage + Supabase
    try {
      const cached = JSON.parse(localStorage.getItem(RECENT_PATIENTS_KEY) || '[]');
      const filtered = cached.filter(p => p.id !== patient.id);
      const updated = [patient, ...filtered].slice(0, 10);
      localStorage.setItem(RECENT_PATIENTS_KEY, JSON.stringify(updated));
    } catch {}
    (async () => {
      const fallbackPid = await getCurrentPracticeId();
      const pid = practiceId || fallbackPid;
      if (!pid) return;
      const { uid } = await getCurrentUserInfo();
      if (!uid) return;
      // Guard: only persist real UUIDs — mock IDs (P001, ASC-P001, etc.) must never reach Supabase
      if (!patient.id || !/^[0-9a-f-]{36}$/i.test(patient.id)) return;
      // Upsert: delete old row for this provider+patient, then insert fresh
      db.from('recent_patients_viewed')
        .delete()
        .eq('practice_id', pid)
        .eq('provider_id', uid)
        .eq('patient_id', patient.id)
        .then(() => {
          db.from('recent_patients_viewed').insert({
            practice_id: pid,
            provider_id: uid,
            patient_id: patient.id,
            viewed_at: new Date().toISOString(),
          }).then(async () => {
            // Trim to last 10 per provider
            const { data: rows } = await db
              .from('recent_patients_viewed')
              .select('id, viewed_at')
              .eq('practice_id', pid)
              .eq('provider_id', uid)
              .order('viewed_at', { ascending: false });
            if (rows && rows.length > 10) {
              const toDelete = rows.slice(10).map(r => r.id);
              await db.from('recent_patients_viewed').delete().in('id', toDelete);
            }
          });
        });
    })();
  }, [practiceId]);

  const handleNavigate = useCallback((dest, params = {}) => {
    if (dest === "new-patient") { setPage("new-patient"); setActivePatient(null); }
    else { setPage(dest); setActivePatient(null); }
    setNavParams(params);
  }, []);

  const handleSavePatient = useCallback(async (form) => {
    try {
      const payload = {
        first_name:              (form.first?.trim()  || form.first_name?.trim())  || '',
        last_name:               (form.last?.trim()   || form.last_name?.trim())   || '',
        date_of_birth:           form.dob             || form.date_of_birth        || null,
        sex:                     form.sex             || null,
        phone:                   form.phone           || null,
        email:                   form.email           || null,
        address:                 form.address         || null,
        city:                    form.city            || null,
        state:                   form.state           || null,
        zip:                     form.zip             || null,
        mrn:                     `OC-${10050 + patients.length}`,
        practice_id:             practiceId,
        insurance_primary:       form.insurance       || null,
        insurance_member_id:     form.memberId        || null,
        insurance_group:         form.insuranceGroup  || null,
        referring_provider:      form.referringProvider     || null,
        primary_care:            form.primaryCareProvider   || null,
        employer:                form.employer        || null,
        attorney:                form.attorney        || null,
        notes:                   form.notes           || null,
        emergency_contact_name:  form.emergencyContactName  || null,
        emergency_contact_phone: form.emergencyContactPhone || null,
      };

      const { data, error } = await db
        .from('patients')
        .insert([payload])
        .select()
        .single();

      if (error) {
        console.error('[new-patient] insert error:', error);
        showNotification('Failed to save patient: ' + error.message, 'error');
        return;
      }

      setPatients(p => [data, ...p]);
      showNotification(`${data.first_name} ${data.last_name} registered successfully`, 'save');
      setPage('patients');
    } catch (err) {
      console.error('[new-patient] unexpected error:', err);
      showNotification('Failed to save patient', 'error');
    }
  // eslint-disable-next-line
  }, [practiceId, patients.length, showNotification]);

  const handleUpdatePatient = useCallback((updated) => {
    setPatients(p => p.map(pt => pt.id === updated.id ? updated : pt));
    setActivePatient(updated);
  }, []);

  const navLabels = {
    dashboard: "Dashboard", schedule: "Schedule", patients: "Patients", chart: "Patient Chart",
    notes: "Notes", coding: "Coding", "new-patient": "New Patient",
    calendar: "Calendar & Billing", transcribe: "Transcribe Visit",
    analytics: "AR Analytics", productivity: "Provider Analytics",
    denials: "Denial Worklist", eligibility: "Eligibility Verification",
    payments: "Payment Posting", imaging: "Imaging", medications: "Medications",
    labs: "Labs", documents: "Documents", fax: "Electronic Fax",
    messages: "Messages", cost: "Surgery Cost Calculator",
    import: "Data Import", portal: "Patient Portal", settings: "Settings",
    "asc-schedule": "OR Schedule", "asc-board": "Case Board",
    "asc-documentation": "Documentation", "asc-discharge": "Discharge",
    "asc-implants": "Implant Log", "asc-analytics": "ASC Analytics", "phone-calls": "Call Log",
    "phone-sms": "SMS / Text", "phone-voicemail": "Voicemails",
    "patient-inbox": "Patient Messages", consults: "Consults",
    "pending-orders": "Pending Orders", "order-sets": "Order Sets", "preference-cards": "Preference Cards",
    "order-labs": "Labs", "order-imaging": "Imaging", "order-medications": "Medications",
  };

  // lazy-load DialerPopup / IncomingCallBanner only when needed
  const LazyDialer = lazy(() => import("./DialerPopup").then(m => ({ default: m.DialerPopup })));
  const LazyIncomingBanner = lazy(() => import("./DialerPopup").then(m => ({ default: m.IncomingCallBanner })));

  return (
    <AppProvider session={session} practiceId={practiceId}>
    <>
      <div className="app">
        <div className={`mob-overlay${sidebarOpen ? " visible" : ""}`} onClick={() => setSidebarOpen(false)} />
        <Sidebar
          active={page === "chart" ? "patients" : page}
          onNav={(dest) => { handleNavigate(dest); setSidebarOpen(false); }}
          theme={theme}
          onThemeChange={handleThemeChange}
          onSignOut={handleSignOut}
          className={sidebarOpen ? "sidebar-mobile-open" : ""}
          practiceId={practiceId}
          subscriptionStatus={subscriptionStatus}
          voicemailEnabled={voicemailEnabled}
          provider={provider}
          onProviderPhotoUpdate={(updated) => setProvider(prev => ({ ...prev, ...updated }))}
        />
        <div className="main" style={isDemoMode ? { paddingBottom: '32px' } : undefined}>
          <div className="topbar">
            <button className="hamburger-btn" onClick={() => setSidebarOpen(o => !o)} aria-label="Menu">☰</button>
            <div>
              <div className="topbar-title">{navLabels[page] || page}</div>
              {activePatient && page === "chart" && <div className="topbar-sub">{activePatient.first_name || activePatient.first} {activePatient.last_name || activePatient.last} · {activePatient.mrn}</div>}
            </div>
            <div className="topbar-spacer" />
            <CommandBar onPatientSelect={handlePatientSelect} onNavigate={handleNavigate} practiceId={practiceId} />
            <div className="topbar-actions">
              <NotificationBell onNavigate={handleNavigate} />
              <button className="btn btn-secondary btn-sm" onClick={() => handleNavigate("new-patient")}>+ New Patient</button>
            </div>
          </div>

          <LabCriticalBanner
            patients={patients}
            onPatientSelect={handlePatientSelect}
            ackedOrders={ackedLabs}
            onAck={(id) => setAckedLabs(s => new Set([...s, id]))}
          />

          {notification && (
            <div style={{ position: "fixed", bottom: 20, right: 20, zIndex: 200 }}>
              <div className={`notification ${notification.type === "info" ? "notification-info" : "notification-warn"} slide-in`} style={{ boxShadow: "0 4px 16px rgba(0,0,0,0.4)" }}>
                <span>✓</span><span>{notification.msg}</span>
              </div>
            </div>
          )}

          {page === "chart" && activePatient ? (
            <ErrorBoundary name="Patient Chart">
              {/* Chart tab strip — appears once a 2nd chart opens. All open
                  charts stay MOUNTED (hidden with display:none) so switching
                  never loses an in-progress note or tab position. Max 3. */}
              {openCharts.length > 1 && (
                <div style={{ display: "flex", alignItems: "center", gap: 6, padding: "6px 12px", background: "var(--navy-2)", borderBottom: "1px solid var(--border)", flexShrink: 0 }}>
                  {openCharts.map(op => {
                    const act = op.id === activePatient.id;
                    return (
                      <div key={op.id} onClick={() => setActivePatient(op)}
                        title={`${op.first_name || op.first} ${op.last_name || op.last} · ${op.mrn || ""}`}
                        style={{ display: "flex", alignItems: "center", gap: 8, padding: "5px 10px", borderRadius: 7, cursor: "pointer", maxWidth: 230,
                                 background: act ? "var(--navy-4)" : "transparent",
                                 border: `1px solid ${act ? "var(--teal)" : "var(--border)"}` }}>
                        <span style={{ fontSize: 12, fontWeight: act ? 700 : 500, color: act ? "var(--text-1)" : "var(--text-2)", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                          {op.first_name || op.first} {op.last_name || op.last}
                        </span>
                        <span style={{ fontSize: 10, color: "var(--text-3)", fontFamily: "var(--mono)", flexShrink: 0 }}>{op.mrn || ""}</span>
                        <button onClick={e => { e.stopPropagation(); closeChart(op.id); }}
                          title="Close chart"
                          style={{ background: "none", border: "none", color: "var(--text-3)", cursor: "pointer", fontSize: 12, padding: 0, lineHeight: 1, flexShrink: 0 }}>✕</button>
                      </div>
                    );
                  })}
                  <span style={{ marginLeft: "auto", fontSize: 10, color: "var(--text-3)" }}>{openCharts.length}/3 charts</span>
                </div>
              )}
              <Suspense fallback={PageLoader}>
                {openCharts.map(op => (
                  <div key={op.id} style={op.id === activePatient.id ? { display: "contents" } : { display: "none" }}>
                    <PatientChart
                      patient={patients.find(p => p.id === op.id) || op}
                      onClose={() => closeChart(op.id)}
                      onCheckin={() => setCheckingIn(true)}
                      onUpdatePatient={handleUpdatePatient}
                      initialTab={chartInitialTab && chartInitialTab.id === op.id ? chartInitialTab.tab : undefined}
                      practiceId={practiceId}
                      onCall={(pt) => {
                        // Demo: mock dialer. Production: tel: link — the staff
                        // member's own phone/softphone dials (no PHI transits us).
                        if (isDemoMode) { setDialerPatient(pt); return; }
                        const ph = (pt?.phone || "").replace(/[^\d+]/g, "");
                        if (ph) window.open(`tel:${ph}`, "_self");
                      }}
                      onSMS={(pt) => {
                        // Demo: mock SMS threads. Production: secure portal
                        // messaging (patient gets a generic SMS nudge — no BAA needed).
                        if (isDemoMode) { setSmsPatient(pt); handleNavigate("phone-sms"); return; }
                        handleNavigate("patient-inbox");
                      }}
                      onNavigate={handleNavigate}
                    />
                  </div>
                ))}
              </Suspense>
            </ErrorBoundary>
          ) : (
            <div className="content">
              <ErrorBoundary name={page}>
                {page === "dashboard"  && <Dashboard onPatientSelect={handlePatientSelect} onNavigate={handleNavigate} />}
                {page === "schedule"   && <SchedulePageComponent onPatientSelect={handlePatientSelect} onCheckin={() => setCheckingIn(true)} patients={patients} />}
                {page === "rooming"    && <RoomingBoard onPatientSelect={handlePatientSelect} patients={patients} />}
                {page === "patients"   && <PatientsPage onPatientSelect={handlePatientSelect} onNavigate={handleNavigate} />}
                {page === "notes"      && <NotesPage patients={patients} onPatientSelect={handlePatientSelect} initialFilter={navParams.filter} practiceId={practiceId} />}
                {page === "coding"     && <Suspense fallback={PageLoader}><CodingPage patients={patients} /></Suspense>}
                {page === "new-patient" && <NewPatientPage onSave={handleSavePatient} prefill={navParams.prefill} prefillSource={navParams.prefillSource} intakeMethod={navParams.intakeMethod} />}
                {page === "calendar"   && calendarPage}
                {page === "transcribe" && transcribePage}
                {page === "analytics"    && <Suspense fallback={PageLoader}><ARDashboard /></Suspense>}
                {page === "mips"         && <Suspense fallback={PageLoader}><MIPSPage patients={patients} onPatientSelect={handlePatientSelect} /></Suspense>}
                {page === "productivity" && <Suspense fallback={PageLoader}><AnalyticsPage practiceId={practiceId} /></Suspense>}
                {page === "outcomes"    && <Suspense fallback={PageLoader}><OutcomesPage onPatientSelect={handlePatientSelect} /></Suspense>}
                {page === "trial-dashboard" && <Suspense fallback={PageLoader}><TrialDashboard /></Suspense>}
                {page === "denials"        && <Suspense fallback={PageLoader}><DenialWorklist /></Suspense>}
                {page === "charge-tickets" && <Suspense fallback={PageLoader}><ChargeTicketPage /></Suspense>}
                {page === "statements"     && <Suspense fallback={PageLoader}><StatementsPage patients={patients} onPatientSelect={handlePatientSelect} /></Suspense>}
                {page === "billing-queue"  && <Suspense fallback={PageLoader}><BillingQueue /></Suspense>}
                {page === "claims-status"  && <Suspense fallback={PageLoader}><ClaimsStatusPage /></Suspense>}
                {page === "eligibility" && <Suspense fallback={PageLoader}><EligibilityPage patients={patients} /></Suspense>}
                {page === "payments"   && <Suspense fallback={PageLoader}><PaymentPostingPage patients={patients} /></Suspense>}
                {page === "imaging"    && <ImagingPage patients={patients} />}
                {page === "medications" && <MedicationsPage patients={patients} onPatientSelect={handlePatientSelect} />}
                {page === "labs"       && <LabsPage patients={patients} onPatientSelect={handlePatientSelect} />}
                {page === "order-labs"        && <LabsPage patients={patients} onPatientSelect={handlePatientSelect} />}
                {page === "order-imaging"     && <ImagingPage patients={patients} />}
                {page === "order-medications" && <MedicationsPage patients={patients} onPatientSelect={handlePatientSelect} />}
                {page === "consults"          && <Suspense fallback={PageLoader}><ConsultsPage patients={patients} /></Suspense>}
                {page === "pending-orders"    && <Suspense fallback={PageLoader}><PendingOrdersPage patients={patients} onPatientSelect={handlePatientSelect} /></Suspense>}
                {page === "order-sets"        && <Suspense fallback={PageLoader}><OrderSetsPage patients={patients} /></Suspense>}
                {page === "preference-cards"  && <Suspense fallback={PageLoader}><PreferenceCardsPage /></Suspense>}
                {page === "documents"  && <Suspense fallback={PageLoader}><DocumentsPage patients={patients} onPatientSelect={handlePatientSelect} onNavigate={handleNavigate} initialTab={navParams.initialTab} /></Suspense>}
                {page === "fax"        && <FaxPage patients={patients} onPatientSelect={handlePatientSelect} />}
                {page === "messages"   && <MessagesPage patients={patients} onPatientSelect={handlePatientSelect} />}
                {page === "patient-inbox" && <PatientInboxPage patients={patients} onPatientSelect={handlePatientSelect} onNavigate={handleNavigate} />}
                {page === "cost"       && <CostEstimatePage patients={patients} />}
                {page === "import"     && <Suspense fallback={PageLoader}><ImportPage onNavigate={handleNavigate} /></Suspense>}
                {page === "portal"     && <PortalManagement />}
                {page === "settings"   && <Suspense fallback={PageLoader}><SettingsPage onNavigate={handleNavigate} theme={theme} onThemeChange={handleThemeChange} /></Suspense>}
                {(page === "asc-schedule" || page === "asc-board" || page === "asc-documentation" || page === "asc-discharge" || page === "asc-implants") && (
                  <Suspense fallback={PageLoader}><ASCModule activePage={page} onNavigate={handleNavigate} practiceId={practiceId} onPatientSelect={handlePatientSelect} /></Suspense>
                )}
                {page === "asc-analytics" && (
                  <Suspense fallback={PageLoader}><ASCAnalyticsPage /></Suspense>
                )}
                {(page === "phone-calls" || page === "phone-sms" || page === "phone-voicemail") && (
                  <Suspense fallback={PageLoader}><VirtualPhoneModule activePage={page} onNavigate={handleNavigate} practiceId={practiceId} patients={patients} onPatientSelect={handlePatientSelect} onIncomingCall={(call) => setIncomingCall(call)} /></Suspense>
                )}
              </ErrorBoundary>
            </div>
          )}
        </div>
      </div>

      {checkingIn && activePatient && (
        <Suspense fallback={null}>
          <CheckInModal
            patient={activePatient}
            onClose={() => setCheckingIn(false)}
            onComplete={() => { showNotification(`${activePatient.first} ${activePatient.last} checked in`); setCheckingIn(false); }}
          />
        </Suspense>
      )}

      {dialerPatient && (
        <Suspense fallback={null}>
          <LazyDialer patient={dialerPatient} practiceId={practiceId} onClose={() => setDialerPatient(null)} />
        </Suspense>
      )}

      {incomingCall && (
        <Suspense fallback={null}>
          <LazyIncomingBanner
            call={incomingCall}
            onDecline={() => setIncomingCall(null)}
            onAnswer={() => {
              const pt = { id: incomingCall.patient_id, first: incomingCall.patient_name?.split(' ')[0] || '', last: incomingCall.patient_name?.split(' ').slice(1).join(' ') || '', phone: incomingCall.phone_number };
              setIncomingCall(null);
              setDialerPatient({ ...pt, isIncoming: true });
            }}
          />
        </Suspense>
      )}

      {sessionWarning && <SessionTimeoutModal secondsLeft={secondsLeft} onStay={stayLoggedIn} />}

      <FeedbackButton />

      {isDemoMode && (
        <div style={{
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
          zIndex: 9999,
          backgroundColor: '#92400e',
          color: '#fef3c7',
          textAlign: 'center',
          padding: '6px 16px',
          fontSize: 12,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 12,
        }}>
          <span>🎯 Demo Mode — Sample data only. No real patient information.</span>
          <a
            href="https://epiphanymd.com"
            target="_blank"
            rel="noopener noreferrer"
            style={{ color: '#fde68a', textDecoration: 'underline', fontWeight: 600 }}
          >
            Get started →
          </a>
        </div>
      )}

      <nav className="mob-nav">
        {[
          { key: "dashboard", icon: "⊞", label: "Home" },
          { key: "patients",  icon: "👤", label: "Patients" },
          { key: "schedule",  icon: "📅", label: "Schedule" },
          { key: "transcribe", icon: "🎙", label: "Notes" },
          { key: "messages",  icon: "💬", label: "More" },
        ].map(({ key, icon, label }) => (
          <button
            key={key}
            className={`mob-nav-item ${(page === key || (key === "patients" && page === "chart")) ? "active" : ""}`}
            onClick={() => handleNavigate(key)}
          >
            <span className="mob-nav-icon">{icon}</span>
            <span className="mob-nav-label">{label}</span>
          </button>
        ))}
      </nav>
    </>
    </AppProvider>
  );
}
